import React, { useState, useEffect } from 'react';
import { CopyIcon, CheckIcon } from './icons/ClipboardIcons';
import { TrashIcon } from './icons/TrashIcon';
import { PlusIcon } from './icons/PlusIcon';
import AddButtonPop from './AddButtonPop';

interface TagItem {
    id: string;
    title: string;
    content: string;
}

const STORAGE_KEY = 'clipboard_tags';

interface TagClipboardProps {
    isAdmin?: boolean;
    onOpenModal?: () => void;
}

const TagClipboard: React.FC<TagClipboardProps> = ({ isAdmin, onOpenModal }) => {
    const [tags, setTags] = useState<TagItem[]>([]);
    const [isAddMode, setIsAddMode] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Load from local storage on mount
    useEffect(() => {
        const savedTags = localStorage.getItem(STORAGE_KEY);
        if (savedTags) {
            setTags(JSON.parse(savedTags));
        }
    }, []);

    // Save to local storage whenever tags change
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tags));
    }, [tags]);

    const handleAddTag = (title: string, content: string) => {
        const newTag: TagItem = {
            id: Date.now().toString(),
            title: title,
            content: content,
        };
        setTags([...tags, newTag]);
    };

    const handleDeleteTag = (id: string) => {
        setTags(tags.filter(tag => tag.id !== id));
    };

    const handleCopy = (id: string, content: string) => {
        navigator.clipboard.writeText(content);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <div className="space-y-4 relative">
            <AddButtonPop
                isOpen={isAddMode}
                onClick={() => setIsAddMode(!isAddMode)}
                onConfirm={handleAddTag}
                isAdmin={true}
                buttonText="Save Tag"
                titlePlaceholder="Title (e.g. Wallet)"
                urlPlaceholder="Content to copy..."
                isTextarea={true}
            />

            {/* Tag List */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {tags.length === 0 && !isAddMode ? (
                    <div className="text-center py-8 text-gray-400 text-xs flex flex-col items-center">
                        <PlusIcon className="w-8 h-8 mb-2 opacity-50" />
                        <span>No tags yet. Click + to add.</span>
                    </div>
                ) : (
                    tags.map((tag) => (
                        <div key={tag.id} className="bg-white/50 p-3 rounded-xl flex items-center justify-between group hover:bg-white/80 transition-all border border-transparent hover:border-fuchsia-100">
                            <div className="flex-1 min-w-0 mr-3">
                                <div className="sidebar-item-title mb-1">{tag.title}</div>
                                <div className="sidebar-item-content truncate bg-white/40 rounded px-1.5 py-1 select-all cursor-text">
                                    {tag.content}
                                </div>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => handleCopy(tag.id, tag.content)}
                                    className={`p-2 rounded-lg transition-all shadow-sm ${copiedId === tag.id
                                        ? 'bg-green-100 text-green-600'
                                        : 'bg-white text-gray-400 hover:text-fuchsia-600 hover:shadow'
                                        }`}
                                    title="Copy content"
                                >
                                    {copiedId === tag.id ? <CheckIcon className="w-4 h-4" /> : <CopyIcon className="w-4 h-4" />}
                                </button>
                                <button
                                    onClick={() => handleDeleteTag(tag.id)}
                                    className="p-2 rounded-lg bg-white/50 hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                    title="Delete tag"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default TagClipboard;
